import { cn } from "@/utils/cn";
import PencomOTPField from "./PencomOTPField";

type FieldType = "text" | "email" | "password" | "tel" | "otp" | "number";

interface PencomTextFieldProps {
  id: string;
  label: string;
  placeholder?: string;
  type: FieldType;
  value: string;
  bg?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextareaChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  className?: string;
  error?: boolean;
  helperText?: string;
  otpLength?: number;
  otpMasked?: boolean;
  otpMaskChar?: string;
  otpMaskDelay?: number;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  multiline?: boolean;
  rows?: number;
  descriptionText?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

const PencomTextField = ({
  id,
  label,
  placeholder,
  type,
  value,
  onChange,
  onTextareaChange,
  className,
  otpLength = 6,
  otpMasked = false,
  otpMaskChar = "⬤",
  otpMaskDelay = 1000,
  disabled,
  onBlur,
  multiline,
  rows = 4,
  error,
  helperText,
  descriptionText,
  bg,
  ...props
}: PencomTextFieldProps) => {
  if (type === "otp") {
    return (
      <PencomOTPField
        id={id}
        label={label}
        length={otpLength}
        value={value}
        onChange={onChange}
        className={className}
        masked={otpMasked}
        maskChar={otpMaskChar}
        maskDelay={otpMaskDelay}
        error={error}
        helperText={helperText}
      />
    );
  }

  // Handle regular input types
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
  };

  // Handle textarea change
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextareaChange?.(e);
  };

  return (
    <div className="flex flex-col gap-y-2">
      <label
        htmlFor={id}
        className="font-clash-display text-medium-dark-grey text-md font-semibold"
      >
        {label}
      </label>
      {descriptionText && (
        <span className="text-medium-grey text-xs font-regular font-clash-display">
          {descriptionText}
        </span>
      )}
      {multiline ? (
        <textarea
          {...props}
          id={id}
          disabled={disabled}
          value={value}
          onChange={handleTextareaChange}
          onBlur={onBlur}
          placeholder={placeholder}
          rows={rows}
          style={{ backgroundColor: bg }}
          className={cn(
            "w-full items-start px-4 font-regular text-sm font-clash-display text-medium-dark-grey py-3 border border-stroke rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent resize-none",
            className,
            {
              "border-red-500": error,
            }
          )}
        />
      ) : (
        <input
          {...props}
          id={id}
          type={type}
          disabled={disabled}
          value={value}
          onChange={handleInputChange}
          onBlur={onBlur}
          placeholder={placeholder}
          style={{ backgroundColor: bg }}
          className={cn(
            "w-full items-start px-4 font-regular text-sm font-clash-display text-medium-dark-grey py-3 border border-stroke rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent",
            className,
            {
              "border-red-500": error,
            }
          )}
        />
      )}
      {helperText && <p className="text-red-500 text-md mt-1 font-clash-display">{helperText}</p>}
    </div>
  );
};

export default PencomTextField;
